import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Modal,
  Box,
  Badge,
  Button,
  TextField
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ChatIcon from '@mui/icons-material/Chat';
import logl2 from '../assets/icons/bannerimg2.png';
import Register from '../components/UserAuthentication/Register';
import Login from '../components/UserAuthentication/Login';
import "../App.css";
import { logout, changePassword, getImage, getUserById, getNotifications, subscribeToPush, buyCopun, addCopun, getCurrentCopunByDiscount, updateNotification, deleteNotification } from '../utils/api';
import EditProfile from '../components/UserAuthentication/EditProfile';
import ChatAi from '../client/pages/ChatAi';
import SetTrainingAlertModal from './SetTrainingAlertModal';
import RefreshIcon from '@mui/icons-material/Refresh'; // Added import
const Navbar = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [username, setUsername] = useState(null);
  const [googleId, setGoogleId] = useState(null);
  const [role, setRole] = useState(null);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [coins, setCoins] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);

  const [permissionStatus, setPermissionStatus] = useState('default');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [isSetAlertOpen, setIsSetAlertOpen] = useState(false);
  const [notificationDays, setNotificationDays] = useState([]);

  // States for cash-in functionality
  const [cashInAnchorEl, setCashInAnchorEl] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
  const [purchasedCoupons, setPurchasedCoupons] = useState([]);
  const [viewPurchasedModalOpen, setViewPurchasedModalOpen] = useState(false);

  // States for confirmation and admin coupon entry
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [enterCodeModalOpen, setEnterCodeModalOpen] = useState(false);
  const [code10, setCode10] = useState('');
  const [code15, setCode15] = useState('');

  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const handleEditProfileOpen = () => setIsEditProfileOpen(true);
  const handleEditProfileClose = () => setIsEditProfileOpen(false);
  const handleNotificationMenuClose = () => setNotificationAnchorEl(null);

  const handleSubscribe = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) throw new Error('No userId found');
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: 'BBRrETqEZhPgfPxhPw9dEoicBHcq91H183kjfcvzMq0GNcePGmLZjAZ9LhJM3CeU9E8jQc_l0bwYkPVVNisgy0w',
        });
        const response = await subscribeToPush(userId, subscription);
      }
      return permission;
    } catch (error) {
      console.error('Subscription error:', error);
      return 'denied';
    }
  };

  const handleNotificationIconClick = async (event) => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    if (permissionStatus === 'default') {
      const permission = await handleSubscribe();
      if (permission === 'granted') setNotificationAnchorEl(event.currentTarget);
    } else if (permissionStatus === 'granted') {
      setNotificationAnchorEl(event.currentTarget);
    } else if (permissionStatus === 'denied') {
      alert('Notifications are disabled. Please enable them in your browser settings.');
    }
  };

  const getTooltipText = (status) => {
    switch (status) {
      case 'default': return 'Click to subscribe to notifications';
      case 'granted': return 'Notifications';
      case 'denied': return 'Notifications are disabled';
      default: return '';
    }
  };
  const markNotificationAsRead = async (notificationId) => {
    try {
      // Update the notification on the server
      await updateNotification(notificationId, { isRead: true });
      // Update local state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif._id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      // Recalculate unread count
      setUnreadCount((prevCount) => prevCount - 1);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  const handleCashIn = async (coinAmount, discount) => {
    if (coins < coinAmount) {
      alert('Not enough coins');
      return;
    }
    try {
      const { copunCode } = await getCurrentCopunByDiscount(discount);
      setCouponCode(copunCode);
      setCountdown(300); // Reset countdown to 5 minutes
      setCouponModalOpen(true);
      setCoins(coins - coinAmount);
      await handleBuyCoupon(coins - coinAmount);
      const newCoupon = { discount, code: copunCode };
      const updatedCoupons = [...purchasedCoupons, newCoupon];
      setPurchasedCoupons(updatedCoupons);
      localStorage.setItem('purchasedCoupons', JSON.stringify(updatedCoupons));
    } catch (error) {
      console.error('Error fetching coupon code:', error);
      alert('Failed to fetch coupon code. Please ensure an admin has added a coupon for this discount.');
    }
  };

  const handleBuyCoupon = async (coins) => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    try {
      await buyCopun(userId, coins);
    } catch (error) {
      console.error('Error buying coupon:', error.message);
    }
  };

  const handleSelectCashInOption = (coinAmount, discount) => {
    setSelectedOption({ coinAmount, discount });
    setIsConfirmModalOpen(true);
    setCashInAnchorEl(null);
  };

  const handleSubmitCodes = async () => {
    try {
      if (code10) await addCopun(code10, 10);
      if (code15) await addCopun(code15, 15);
      setEnterCodeModalOpen(false);
      setCode10('');
      setCode15('');
      alert('Coupon codes submitted successfully');
    } catch (error) {
      console.error('Error submitting coupon codes:', error);
      alert('Failed to submit coupon codes');
    }
  };

  useEffect(() => {
    const storedCoupons = localStorage.getItem('purchasedCoupons');
    if (storedCoupons) {
      setPurchasedCoupons(JSON.parse(storedCoupons));
    }
  }, []);

  useEffect(() => {
    let timer;
    if (couponModalOpen && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [couponModalOpen, countdown]);

  useEffect(() => {
    const fetchUserData = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      try {
        const userData = await getUserById(userId);
        setCoins(userData.coins || 0);
        setNotificationDays(userData.notificationDays || []);
        setEmailVerified(userData.emailVerified);
        const { image } = await getImage(userId);
        setUploadedImage(image);
        const notificationsData = await getNotifications(userId);
        setNotifications(notificationsData);
        setUnreadCount(notificationsData.filter(notif => !notif.isRead).length);
      } catch (error) {
        console.error('Error fetching user data:', error.message);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1370);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedGoogleId = localStorage.getItem('googleId');
    const storedRole = localStorage.getItem('role');
    if (storedUsername) setUsername(storedUsername);
    if (storedGoogleId) setGoogleId(storedGoogleId);
    if (storedRole) setRole(storedRole);
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      // Call the logout API
      await logout(username);

      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear all cookies
      document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });

      // Clear IndexedDB databases
      if (window.indexedDB) {
        const dbs = await window.indexedDB.databases();
        dbs.forEach((db) => {
          window.indexedDB.deleteDatabase(db.name);
        });
      }

      // Clear Cache Storage
      if (window.caches) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Unregister Service Workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      // Reset state
      setUsername(null);
      handleMenuClose();

      // Reload the page to ensure a clean state
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error.message);
      alert('Error during logout');
    }
  };
  const handleSelectRole = () => {
    const userId = localStorage.getItem('userId');
    handleMenuClose();
    navigate(`/select-role?userId=${userId}`);
  };

  const handlePasswordChangeSubmit = async ({ currentPassword, newPassword, confirmPassword }) => {
    try {
      await changePassword(username, currentPassword, newPassword, confirmPassword);
      setIsChangePasswordOpen(false);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to change password');
    }
  };
  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId); // Call the API
      const deletedNotif = notifications.find((notif) => notif._id === notificationId);
      setNotifications(notifications.filter((notif) => notif._id !== notificationId));
      if (!deletedNotif.isRead) {
        setUnreadCount(unreadCount - 1); // Decrement unread count if the notification was unread
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };
  const navLinks = [
    { text: 'תרגילים', path: '/#exercises' },
    { text: 'שעון', path: '/Timer' },
    { text: 'משקל', path: '/weight' },
    { text: 'תוכנית אימונים והעלאות', path: '/program' },
    { text: 'בניית תוכנית אימונים', path: '/workoutProgram', requiredRole: 'trainer' },
    { text: 'תפריטי תזונה', path: '/nutrition' },
    { text: 'חפש מוצרים', path: '/foodTrack' },
    { text: 'גלריה', path: '/gallery' },
    { text: 'אירועים', path: '/adminAd' },
    { text: 'מתכונים שווים', path: '/recipes' },
    { text: 'התאמת מאמן', path: '/hunt' },
    { text: 'פורטל מאמנים', path: '/portal', requiredRole: 'trainer' },
    { text: 'מותג', path: '/brand' },
  ];

  const filteredNavLinks = navLinks.filter(link => !link.requiredRole || link.requiredRole === role);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        width: '100%',
        padding: '10px 20px',
        backgroundColor: '#B5A78A',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      {!isMobile ? (
        <Stack direction="row" gap="30px" sx={{ flex: 1, justifyContent: 'center' }}>
          {filteredNavLinks.map((link) => (
            <Link
              key={link.text}
              to={link.path}
              style={{
                textAlign: 'right',
                direction: 'rtl',
                textDecoration: 'none',
                color: '#3A1212',
                fontWeight: '600',
                fontFamily: 'GveretLevin',
                letterSpacing: '0.5px',
              }}
            >
              {link.text}
            </Link>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" alignItems="center" gap="10px">
        {isMobile && (
          <IconButton onClick={() => setDrawerOpen(true)} sx={{ position: 'absolute', left: 20 }}>
            <MenuIcon />
          </IconButton>
        )}
        <Tooltip title={username ? `Hello, ${username}` : 'Login/Register'}>
          <IconButton onClick={handleMenuOpen} sx={{ position: 'absolute', left: 60 }}>
            {((localStorage.getItem('googleId') === 'undefined' || localStorage.getItem('profileImage') === 'undefined') && uploadedImage) ? (
              <img
                src={uploadedImage}
                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <>
                {googleId ? (
                  <img
                    src={uploadedImage}
                    alt="User Avatar"
                    style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <PersonIcon />
                )}
              </>
            )}
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          direction="rtl"
          textAlign="right"
        >
          {!username ? (
            <>
              <MenuItem onClick={() => { handleMenuClose(); setRegisterOpen(true); }}>רישום</MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); setLoginOpen(true); }}>התחברות</MenuItem>
            </>
          ) : (
            <>
              {!googleId ? (
                <>
                  <MenuItem onClick={handleEditProfileOpen}>ערוך חשבון</MenuItem>
                  <MenuItem onClick={handleLogout}>התנתקות</MenuItem>
                </>
              ) : (
                <>
                  {(role === 'pending' || !emailVerified) && (
                    <MenuItem onClick={handleSelectRole}>בחירת מטרה / אימות משתמש</MenuItem>
                  )}
                  <MenuItem onClick={handleLogout}>התנתקות</MenuItem>
                </>
              )}
            </>
          )}
        </Menu>
      </Stack>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Stack sx={{ width: '250px', padding: '20px' }}>
          <IconButton sx={{ alignSelf: 'flex-start' }} onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
          <List>
            {filteredNavLinks.map((link) => (
              <ListItem button key={link.text} onClick={() => setDrawerOpen(false)}>
                <Link
                  to={link.path}
                  style={{
                    textDecoration: 'none',
                    color: '#3A1212',
                    width: '100%',
                    textAlign: 'right',
                    direction: 'rtl',
                  }}
                >
                  <ListItemText primary={link.text} />
                </Link>
              </ListItem>
            ))}
          </List>
        </Stack>
      </Drawer>

      <Register open={registerOpen} onClose={() => setRegisterOpen(false)} onLoginClick={() => setLoginOpen(true)} />
      <Login open={loginOpen} onClose={() => setLoginOpen(false)} onRegisterClick={() => setRegisterOpen(true)} />
      <EditProfile
        open={isEditProfileOpen}
        onClose={handleEditProfileClose}
        onPasswordSubmit={handlePasswordChangeSubmit}
        userId={localStorage.getItem('userId')}
      />
      <Modal
        open={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        aria-labelledby="chat-modal-title"
        aria-describedby="chat-modal-description"
      >
        <Box className='modalStyle'>
          <ChatAi />
        </Box>
      </Modal>

      <Box alignItems="center" justifyContent="center" display="flex">
        {username && (
          <Stack direction="row" alignItems="center" gap="5px">
            <IconButton onClick={(event) => setCashInAnchorEl(event.currentTarget)}>
              <MonetizationOnIcon sx={{ color: '#FFD700' }} />
              <Typography sx={{ color: '#FFD700', fontWeight: 'bold' }}>{coins}</Typography>
            </IconButton>
          </Stack>
        )}

        <Tooltip title="Refresh page">
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {username && 'Notification' in window && 'serviceWorker' in navigator && (
          <Tooltip title={getTooltipText(permissionStatus)}>
            <IconButton onClick={handleNotificationIconClick}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          textAlign="right"
          direction="rtl"
        >
          <MenuItem
            textAlign="right"
            direction="rtl"
            onClick={() => setIsSetAlertOpen(true)}>ימי התראות</MenuItem>
          {notifications.length > 0 ? (
            notifications.map((notif) => (
              <MenuItem
                key={notif._id}
                onClick={() => markNotificationAsRead(notif._id)}
                onContextMenu={(e) => {
                  e.preventDefault(); // Prevent default browser context menu
                  setDeleteAnchorEl(e.currentTarget); // Set anchor for delete menu
                  setSelectedNotification(notif._id); // Track the selected notification
                }}
              >
                {notif.message}
              </MenuItem>
            ))
          ) : (
            <MenuItem textAlign="right" direction="rtl">
              אין הודעות
            </MenuItem>
          )}
        </Menu>
        <Menu
          anchorEl={deleteAnchorEl}
          open={Boolean(deleteAnchorEl)}
          onClose={() => {
            setDeleteAnchorEl(null);
            setSelectedNotification(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              handleDeleteNotification(selectedNotification);
              setDeleteAnchorEl(null);
              setSelectedNotification(null);
            }}
          >
            Delete
          </MenuItem>
        </Menu>


        <SetTrainingAlertModal
          open={isSetAlertOpen}
          onClose={() => setIsSetAlertOpen(false)}
          currentDays={notificationDays}
          userId={localStorage.getItem('userId')}
        />
        <Tooltip title="Talk with Agent">
          <IconButton onClick={() => setChatModalOpen(true)}>
            <ChatIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={cashInAnchorEl}
          open={Boolean(cashInAnchorEl)}
          onClose={() => setCashInAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {role === 'admin' && (
            <MenuItem onClick={() => {
              setEnterCodeModalOpen(true);
              setCashInAnchorEl(null);
            }}>
              תיצור קופון
            </MenuItem>
          )}
          {purchasedCoupons.length > 0 && (
            <MenuItem onClick={() => {
              setViewPurchasedModalOpen(true);
              setCashInAnchorEl(null);
            }}>
              קופונים שלי
            </MenuItem>
          )}
          <MenuItem
            onClick={() => handleSelectCashInOption(100, 10)}
            disabled={coins < 100}
          >
            10% הנחה
          </MenuItem>
          <MenuItem
            onClick={() => handleSelectCashInOption(200, 15)}
            disabled={coins < 200}
          >
            15% הנחה
          </MenuItem>
        </Menu>
        <Modal
          open={couponModalOpen}
          onClose={() => setCouponModalOpen(false)}
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4
          }}>
            <Typography textAlign="right" color="#B5A78A" direction="rtl" variant="h6" component="h2">
              קוד קופון
            </Typography>
            <Typography textAlign="right" sx={{ mt: 2 }}>
              {couponCode}
            </Typography>
            <Typography textAlign="right" sx={{ mt: 2 }}>
              זמן נותר: {formatTime(countdown)}
            </Typography>
          </Box>
        </Modal>
        <Modal
          open={viewPurchasedModalOpen}
          onClose={() => setViewPurchasedModalOpen(false)}
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4
          }}>
            <Typography variant="h6" component="h2">
              Purchased Coupons
            </Typography>
            {purchasedCoupons.map((coupon, index) => (
              <Typography key={index} sx={{ mt: 2 }}>
                {coupon.discount}% discount: {coupon.code}
              </Typography>
            ))}
          </Box>
        </Modal>
        <Modal
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
        >
          <Box
            sx={{
              direction: 'rtl',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 300,
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography fontFamily="GveretLevin" color="#B5A78A" variant="h6" component="h2">
              אישור רכישה
            </Typography>
            <Typography sx={{ mt: 2 }}>
              האם אתה בטוח שברצונך לרכוש קופון תמורת {selectedOption?.coinAmount} מטבעות לקבלת {selectedOption?.discount}% הנחה?
            </Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
              <Button
                onClick={() => {
                  handleCashIn(selectedOption.coinAmount, selectedOption.discount);
                  setIsConfirmModalOpen(false);
                }}
              >
                כן
              </Button>
              <Button onClick={() => setIsConfirmModalOpen(false)}>לא</Button>
            </Stack>
          </Box>
        </Modal>
        <Modal
          open={enterCodeModalOpen}
          onClose={() => setEnterCodeModalOpen(false)}
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4
          }}>
            <Typography variant="h6" component="h2">
              Enter Coupon Codes
            </Typography>
            <TextField
              label="10% Discount Code"
              value={code10}
              onChange={(e) => setCode10(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <TextField
              label="15% Discount Code"
              value={code15}
              onChange={(e) => setCode15(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Button
              onClick={handleSubmitCodes}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Submit
            </Button>
          </Box>
        </Modal>
        <Link to="/">
          <img src={logl2} alt="logo" style={{ width: '40px', height: '40px' }} />
        </Link>
      </Box>
    </Stack>
  );
};

export default Navbar;